import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface RowDailyNotePopoverProps {
  /** Existing saved note for this day cell. */
  note: string;
  /** In-flight note text while the popover is open. */
  draftNote: string;
  isOpen: boolean;
  /** Whether a note is mandatory for this cell (description required). */
  isRequired: boolean;
  /** Header label, e.g. "Monday 3 Feb · Project name". */
  headerLabel: string;
  onOpen: () => void;
  onDraftChange: (value: string) => void;
  /** Persist the draft note. */
  onSave: () => void;
  onClose: () => void;
}

/** Notes popover attached to a single day's hours cell. */
export function RowDailyNotePopover({
  note,
  draftNote,
  isOpen,
  isRequired,
  headerLabel,
  onOpen,
  onDraftChange,
  onSave,
  onClose,
}: RowDailyNotePopoverProps) {
  const hasNote = note.trim().length > 0;

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && isOpen) {
          // Save draft on close
          onSave();
        }
        if (open) {
          onOpen();
        } else {
          onClose();
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          className={cn(
            'flex items-center justify-center rounded p-0.5 transition-colors shrink-0',
            hasNote
              ? 'text-primary hover:text-primary/80'
              : isRequired
                ? 'text-destructive ring-1 ring-destructive/40 rounded hover:text-destructive/80'
                : 'text-muted-foreground/30 hover:text-muted-foreground/70'
          )}
          onClick={onOpen}
          type="button"
        >
          <MessageSquare className={cn('h-3 w-3', hasNote && 'fill-current')} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="center" side="bottom">
        <div className="space-y-2">
          <p className="text-xs font-medium text-foreground">{headerLabel}</p>
          <Textarea
            autoFocus
            value={draftNote}
            onChange={(e) => onDraftChange(e.target.value)}
            placeholder="Add a note for this day..."
            className="h-20 min-h-[80px] resize-none text-xs"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                onSave();
                onClose();
              }}
            >
              Done
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
